import {
  Component,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { Router } from '@angular/router';
import {
  IonButton,
  IonIcon,
  IonModal,
  ModalController
} from '@ionic/angular/standalone';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { HealthScoreDisclaimerComponent } from '@components/health-score/disclaimer/disclaimer.component';
import {
  selectReminderFaceScanStatus,
  selectReminderLatestFaceScanProcess
} from '@components/reminder/reminder.selectors';
import {
  DnaSkinFaceAnalyzeProcess,
  DnaSkinFaceScanActionType,
  DnaSkinFaceScanProcess,
  StatusEligibilitySkinFaceType
} from '@interfaces/schema';
import { ReportSkinHealthGuidelineAnalysisActions } from '@pages/report/skin/health-guideline/analysis/analysis.actions';
import { selectReportSkinFindLatestByUserResponse } from '@pages/report/skin/health-guideline/analysis/analysis.selectors';
import { ReportSkinHealthGuidelineTakePictureActions } from '@pages/report/skin/health-guideline/take-picture/take-picture.actions';
import { CdnPipe } from '@pipes/cdn/cdn.pipe';
import { Session } from '@providers/session/session.service';

/**
 * Nourish questionnaire prompt modal.
 */
@Component({
  selector: 'app-skin-prompt-modal',
  templateUrl: './prompt-modal.component.html',
  styleUrls: ['./prompt-modal.component.scss'],
  imports: [
    IonModal,
    IonButton,
    IonIcon,
    TranslateModule,
    HealthScoreDisclaimerComponent
  ],
  providers: [CdnPipe]
})
export class SkinPromptModalComponent {
  private readonly cdn = inject(CdnPipe);
  private readonly modalController = inject(ModalController);
  private readonly router = inject(Router);
  private readonly session = inject(Session);
  private readonly store = inject(Store);

  private readonly skinPromptModal = viewChild<IonModal>('skinPromptModal');

  readonly isOpen = input.required<boolean | null>();
  readonly isOpenChange = output<boolean>();

  private readonly faceScanStatus =
    signal<StatusEligibilitySkinFaceType | null>(null);
  private readonly findLatestByUserResponse =
    signal<DnaSkinFaceAnalyzeProcess | null>(null);
  private readonly latestFaceScanProcess =
    signal<DnaSkinFaceScanProcess | null>(null);
  modal: {
    disclaimer: boolean;
  } = {
    disclaimer: false
  };

  destroy$ = new Subject<boolean>();

  /**
   * Close other modals.
   *
   * @description This method closes any other modals that are currently open.
   * @returns {Promise<void>}
   * @memberof SkinPromptModalComponent
   */
  async closeOtherModals(): Promise<void> {
    let count = 0;
    while ((await this.modalController.getTop()) && count < 5) {
      await this.modalController.dismiss();
    }
  }

  /**
   *  Go to skin face scan page.
   *
   * @description This method navigates the user to the skin face scan page.
   * @memberof SkinPromptModalComponent
   */
  goToSkinFaceScan(): void {
    this.closeOtherModals();
    this.router.navigate(['/report/SKIN/health-guideline/take-picture']);
  }

  /**
   * Go to skin face scan analysis page.
   *
   * @description This method navigates the user to the skin face scan analysis page.
   * @memberof SkinPromptModalComponent
   */
  goToSkinFaceScanAnalysis(): void {
    this.closeOtherModals();
    this.router.navigate(['report/SKIN/health-guideline/analysis'], {
      queryParams: {
        from: 'prompt-modal'
      }
    });
  }

  /**
   * Go to skin face scan preview page.
   *
   * @description This method navigates the user to the skin face scan preview page.
   * @memberof SkinPromptModalComponent
   */
  goToSkinFaceScanPreview(): void {
    this.closeOtherModals();
    this.router.navigate(
      ['report/SKIN/health-guideline/take-picture/preview'],
      {
        queryParams: {
          from: 'prompt-modal'
        }
      }
    );
  }

  /**
   * Let get started.
   *
   * @memberof SkinPromptModalComponent
   */
  letGetStarted(): void {
    // Store latest face scan process ids in session storage
    // This is used to identify if new prosess or not
    if (this.latestFaceScanProcess()?.id) {
      this.session.store(
        this.session.keys.idLatestFaceScanProcess,
        this.latestFaceScanProcess()?.id
      );
    } else {
      this.session.clear(this.session.keys.idLatestFaceScanProcess);
    }
    if (this.findLatestByUserResponse()?.id) {
      this.session.store(
        this.session.keys.idLatestDataSkinFaceAnalyzeProcess,
        this.findLatestByUserResponse()?.id
      );
    } else {
      this.session.clear(this.session.keys.idLatestDataSkinFaceAnalyzeProcess);
    }

    if (
      this.faceScanStatus() === StatusEligibilitySkinFaceType.FailedScan ||
      this.faceScanStatus() === StatusEligibilitySkinFaceType.NeedScan
    ) {
      this.goToSkinFaceScan();
      return;
    }

    if (
      this.faceScanStatus() === StatusEligibilitySkinFaceType.InProgressScan
    ) {
      this.store.dispatch(
        ReportSkinHealthGuidelineTakePictureActions.setImage({
          image: this.cdn.transform(this.latestFaceScanProcess()?.pointer ?? '')
        })
      );
      this.store.dispatch(
        ReportSkinHealthGuidelineTakePictureActions.initiateUploadImageSubscription()
      );

      this.goToSkinFaceScanPreview();
      return;
    }

    if (
      this.faceScanStatus() === StatusEligibilitySkinFaceType.SuccessScan ||
      this.faceScanStatus() === StatusEligibilitySkinFaceType.FailedAnalyze
    ) {
      this.store.dispatch(
        ReportSkinHealthGuidelineTakePictureActions.setImage({
          image: this.cdn.transform(this.latestFaceScanProcess()?.pointer ?? '')
        })
      );
      this.store.dispatch(
        ReportSkinHealthGuidelineTakePictureActions.uploadImageSubscriptionDataReceived(
          {
            data: {
              action: DnaSkinFaceScanActionType.Completed,
              data: this.latestFaceScanProcess() as DnaSkinFaceScanProcess
            }
          }
        )
      );

      this.goToSkinFaceScanPreview();
      return;
    }

    if (
      this.faceScanStatus() === StatusEligibilitySkinFaceType.InProgressAnalyze
    ) {
      // Reinitiate face analysis subscription
      this.store.dispatch(
        ReportSkinHealthGuidelineAnalysisActions.initiateFaceAnalysisSubscription()
      );
      // Set the isContinueLastProcess to true
      this.store.dispatch(
        ReportSkinHealthGuidelineAnalysisActions.setIsContinueLastProcess({
          isContinueLastProcess: true
        })
      );
      // Dispatch the uploadImageSubscriptionDataReceived action with the latest skinFaceScanProcess data
      this.store.dispatch(
        ReportSkinHealthGuidelineTakePictureActions.uploadImageSubscriptionDataReceived(
          {
            data: {
              data: this.findLatestByUserResponse()?.skinFaceScanProcess
            }
          }
        )
      );

      this.goToSkinFaceScanAnalysis();
      return;
    }

    if (
      this.faceScanStatus() === StatusEligibilitySkinFaceType.SuccessAnalyze
    ) {
      // If the generationEndAt is set and submitted At is null,
      // set the isLastProcessHasFinished to true
      this.store.dispatch(
        ReportSkinHealthGuidelineAnalysisActions.setIsLastProcessHasFinished({
          isLastProcessHasFinished: true
        })
      );
      // Dispatch the uploadImageSubscriptionDataReceived action with the latest skinFaceScanProcess data
      this.store.dispatch(
        ReportSkinHealthGuidelineTakePictureActions.uploadImageSubscriptionDataReceived(
          {
            data: {
              data: this.findLatestByUserResponse()?.skinFaceScanProcess
            }
          }
        )
      );
      // Dispatch the faceAnalysisSubscriptionResponse action with the latest summaryHighlight data
      // This is to ensure that the latest summaryHighlight is available for the analysis page
      this.store.dispatch(
        ReportSkinHealthGuidelineAnalysisActions.faceAnalysisSubscriptionResponse(
          {
            data: {
              action: DnaSkinFaceScanActionType.Completed,
              data: this.findLatestByUserResponse() as DnaSkinFaceAnalyzeProcess
            }
          }
        )
      );

      this.goToSkinFaceScanAnalysis();
    }
  }

  /**
   * On did dismiss modal.
   *
   * @memberof SkinPromptModalComponent
   */
  onDidDismissModal(): void {
    const modal = this.skinPromptModal();
    if (modal) {
      modal.showBackdrop = true;
    }
    this.isOpenChange.emit(false);

    // Clean up the destroy subject to prevent memory leaks
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /**
   * On did present modal.
   *
   * @description This method is called when the modal is presented.
   * @memberof SkinPromptModalComponent
   */
  onDidPresentModal(): void {
    this.store
      .select(selectReportSkinFindLatestByUserResponse)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.findLatestByUserResponse.set(response);
      });

    this.store
      .select(selectReminderFaceScanStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe((faceScanStatus) => {
        this.faceScanStatus.set(faceScanStatus);
      });

    this.store
      .select(selectReminderLatestFaceScanProcess)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.latestFaceScanProcess.set(response);
      });
  }

  /**
   * On will dismiss modal.
   *
   * @description This method is called when the modal is about to be dismissed.
   * @memberof SkinPromptModalComponent
   */
  onWillDismissModal(): void {
    const modal = this.skinPromptModal();
    if (modal) {
      modal.showBackdrop = false;
    }
  }
}
